import { TokenInfo } from '@labb/constellation-core-types';
import { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { useMainContext } from '../context/MainContext.tsx';
import { apiPostPegaIdvCallback } from '../journey/pega/apiPostPegaIdvCallback.ts';
import { API_ERROR_CODES } from '../onboarding-api';
import logger from '../utils/logger.ts';
import { normalizeSingpassUserData } from './normalizeSingpassUserData.ts';
import {
  SingpassClientState,
  SingpassCodesResponse,
  SingpassResult,
  SingpassStatus,
  SingpassUserData,
} from './singpassTypes.ts';

const SINGPASS_CALLBACK_PATH = '/singpass-callback';
const openApiBaseUrl = import.meta.env.VITE_OPENAPI_BASE_URL;
const pegaBaseUrl = import.meta.env.VITE_PEGA_BASE_URL;

const parseSingpassCodesResponse = (singpassCodesResponse?: SingpassCodesResponse) => {
  const clientState: SingpassClientState = singpassCodesResponse?.clientState
    ? JSON.parse(singpassCodesResponse.clientState)
    : undefined;

  const urlSearchParams = new URLSearchParams(clientState?.urlSearchParams || '');
  urlSearchParams.delete('embedded'); // when coming back from Singpass we are no longer embedded

  return {
    caseId: singpassCodesResponse?.applicationId,
    urlSearchParams,
    pegaToken: clientState?.pegaToken,
    csrfToken: clientState?.csrfToken,
  };
};

export function useSingpassCallback(): { isLoading: boolean; assignmentKey?: string; errorCode?: API_ERROR_CODES } {
  const location = useLocation();
  const history = useHistory();
  const { setPegaToken, setCsrfToken, setUrlSearchParams } = useMainContext();
  const [isLoading, setIsLoading] = useState(true);
  const [assignmentKey, setAssignmentKey] = useState<string | undefined>();
  const [errorCode, setErrorCode] = useState<API_ERROR_CODES | undefined>();

  const navigateToApplicationForm = useCallback(
    (urlSearchParams?: URLSearchParams, pegaToken?: TokenInfo, csrfToken?: string) => {
      if (pegaToken) {
        setPegaToken(pegaToken);
      }

      if (csrfToken) {
        setCsrfToken(csrfToken);
      }

      history.replace(urlSearchParams ? `/?${urlSearchParams.toString()}` : '/');
    },
    [history, setCsrfToken, setPegaToken]
  );

  // Posts the Singpass result to Pega and stores the resulting assignment key (or an error).
  const submitPegaIdvCallback = useCallback(async (pegaToken: TokenInfo, result: SingpassResult) => {
    const { data, errorCode: idvErrorCode } = await apiPostPegaIdvCallback(pegaBaseUrl, pegaToken, result);
    if (data?.assignmentKey) {
      setAssignmentKey(data.assignmentKey);
    } else {
      logger.error(`Call to Pega /idv-callback endpoint failed`);
      setErrorCode(idvErrorCode || API_ERROR_CODES.GENERIC);
    }
  }, []);

  const handleSingpassSuccess = useCallback(
    async (singpassCodesResponse: SingpassCodesResponse, userData: SingpassUserData) => {
      const { caseId, urlSearchParams, pegaToken, csrfToken } = parseSingpassCodesResponse(singpassCodesResponse);

      await submitPegaIdvCallback(pegaToken, {
        caseId: caseId ?? '',
        status: SingpassStatus.SUCCESS,
        userData: normalizeSingpassUserData(userData),
      });

      navigateToApplicationForm(urlSearchParams, pegaToken, csrfToken);
    },
    [submitPegaIdvCallback, navigateToApplicationForm]
  );

  /**
   * This can be an actual error OR the user not consenting, in which case we need to still continue
   */
  const handleSingpassError = useCallback(
    async (singpassCodesResponse?: SingpassCodesResponse, error?: string, errorDescription?: string) => {
      logger.error(`Singpass failed: ${error} ${errorDescription}`);

      const { caseId, urlSearchParams, pegaToken, csrfToken } = parseSingpassCodesResponse(singpassCodesResponse);

      if (pegaToken) {
        await submitPegaIdvCallback(pegaToken, {
          caseId: caseId ?? '',
          status: SingpassStatus.ERROR,
          error,
          errorDescription,
        });
      } else {
        setErrorCode(API_ERROR_CODES.GENERIC);
      }

      navigateToApplicationForm(urlSearchParams, pegaToken, csrfToken);
    },
    [submitPegaIdvCallback, navigateToApplicationForm]
  );

  // Not on the callback route: persist any url params (they get stripped when embedded) and stop.
  const saveUrlParamsAndSkip = useCallback(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    if ([...urlSearchParams.keys()].length > 0) {
      setUrlSearchParams(urlSearchParams);
    }
    setIsLoading(false);
  }, [setUrlSearchParams]);

  const processCallback = useCallback(async () => {
    const params = new URLSearchParams(location.search);
    const error = params.get('error');
    const errorDescription = params.get('error_description') ?? undefined;
    const code = params.get('code') || '';
    const state = params.get('state') || '';
    const iss = params.get('iss') || '';

    try {
      // Always call the /codes endpoint first so we can try to restore the gateway state
      const codesResponse = await fetch(`${openApiBaseUrl}/v1/singpass/codes?state=${state}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code, iss }),
      });
      if (!codesResponse.ok) {
        await handleSingpassError(undefined, 'Error calling /singpass/codes endpoint');
        return;
      }
      const codesResponseBody: SingpassCodesResponse = await codesResponse.json();

      if (error) {
        // this handles the scenerio where singpass was not consented too. ie the user will still
        // be able to continue the journey, but we do not need to request the user data
        await handleSingpassError(codesResponseBody, error, errorDescription);
        return;
      }

      // call /user-data endpoint to get the singpass user data
      const userDataResponse = await fetch(`${openApiBaseUrl}/v1/singpass/user-data`, {
        credentials: 'include',
      });
      if (!userDataResponse.ok) {
        await handleSingpassError(codesResponseBody, 'Error calling /singpass/user-data endpoint');
        return;
      }
      const userDataResponseBody = await userDataResponse.json();

      await handleSingpassSuccess(codesResponseBody, userDataResponseBody);
    } catch (err) {
      await handleSingpassError(undefined, err instanceof Error ? err.message : 'Unexpected error');
    } finally {
      setIsLoading(false);
    }
  }, [location.search, handleSingpassError, handleSingpassSuccess]);

  useEffect(() => {
    if (location.pathname !== SINGPASS_CALLBACK_PATH) {
      saveUrlParamsAndSkip();
      return;
    }

    void processCallback();
  }, [location.pathname, saveUrlParamsAndSkip, processCallback]);

  return {
    isLoading,
    assignmentKey,
    errorCode,
  };
}
